import React from 'react';
import { Container, Box, Typography, Chip, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import {
  AccountBalance, Inventory, People, Payment, Assessment,
  AttachMoney, Business, QrCode2, PointOfSale, Stars,
  CheckCircle, AutoAwesome,
  Factory, AccountTree, HistoryEdu, RocketLaunch
} from '@mui/icons-material';
import WebsiteLayout from '../../components/Website/WebsiteLayout';
import HeroSection from '../../components/Website/HeroSection';
import SEO from '../../components/SEO/SEO';
import { getPageSEO } from '../../config/seo';
import { useTranslation } from 'react-i18next';

import AccountingMockup from '../../components/Website/mockups/AccountingMockup';
import InventoryMockup from '../../components/Website/mockups/InventoryMockup';
import CRMMockup from '../../components/Website/mockups/CRMMockup';
import PayrollMockup from '../../components/Website/mockups/PayrollMockup';
import ReportingMockup from '../../components/Website/mockups/ReportingMockup';
import POSMockup from '../../components/Website/mockups/POSMockup';
import LoyaltyMockup from '../../components/Website/mockups/LoyaltyMockup';
import MultiCurrencyMockup from '../../components/Website/mockups/MultiCurrencyMockup';
import MultiCompanyMockup from '../../components/Website/mockups/MultiCompanyMockup';
import QRCodeMockup from '../../components/Website/mockups/QRCodeMockup';

// ── single module row ─────────────────────────────────────────────────────────
const ModuleRow = ({ module, index }) => {
  const isEven = index % 2 === 0;

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: theme =>
          index % 2 === 0
            ? theme.palette.mode === 'dark' ? 'background.default' : 'white'
            : theme.palette.mode === 'dark' ? 'background.paper' : 'grey.50',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: isEven ? 'row' : 'row-reverse' },
            alignItems: 'center',
            gap: { xs: 6, md: 10 },
          }}
        >
          {/* text side */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 48, height: 48, borderRadius: 2,
                  bgcolor: 'primary.main', color: 'primary.contrastText',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {module.icon}
              </Box>
              <Chip
                label={module.tag}
                size="small"
                sx={{
                  bgcolor: 'primary.main', color: 'primary.contrastText',
                  fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.04em',
                  opacity: 0.85,
                }}
              />
            </Box>

            <Typography variant="h2" sx={{ fontSize: { xs: 28, md: 38 }, fontWeight: 800, mb: 1, lineHeight: 1.2 }}>
              {module.title}
            </Typography>
            <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 2, fontWeight: 500 }}>
              {module.subtitle}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3.5, lineHeight: 1.75, fontSize: 16 }}>
              {module.description}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              {Array.isArray(module.items) && module.items.map((item) => (
                <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
                  <CheckCircle sx={{ color: 'secondary.main', fontSize: 18, mt: '2px', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontSize: 14.5, color: 'text.primary' }}>{item}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* mockup side */}
          <Box
            sx={{
              flex: 1, minWidth: 0,
              display: 'flex',
              justifyContent: { xs: 'center', md: isEven ? 'flex-end' : 'flex-start' },
            }}
          >
            {module.mockup}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

// ── page ──────────────────────────────────────────────────────────────────────
const FeaturesPage = () => {
  const { t } = useTranslation();
  const seo = getPageSEO('features');

  const modules = [
    {
      id: 'financials',
      icon: <AccountBalance sx={{ fontSize: 28 }} />,
      tag: t('website.features.modules.financials.tag'),
      title: t('website.features.modules.financials.title'),
      subtitle: t('website.features.modules.financials.subtitle'),
      description: t('website.features.modules.financials.description'),
      items: t('website.features.modules.financials.items', { returnObjects: true }),
      mockup: <AccountingMockup />,
    },
    {
      id: 'supply-chain',
      icon: <Inventory sx={{ fontSize: 28 }} />,
      tag: t('website.features.modules.supplyChain.tag'),
      title: t('website.features.modules.supplyChain.title'),
      subtitle: t('website.features.modules.supplyChain.subtitle'),
      description: t('website.features.modules.supplyChain.description'),
      items: t('website.features.modules.supplyChain.items', { returnObjects: true }),
      mockup: <InventoryMockup />,
    },
    {
      id: 'manufacturing',
      icon: <Factory sx={{ fontSize: 28 }} />,
      tag: t('website.features.modules.manufacturing.tag'),
      title: t('website.features.modules.manufacturing.title'),
      subtitle: t('website.features.modules.manufacturing.subtitle'),
      description: t('website.features.modules.manufacturing.description'),
      items: t('website.features.modules.manufacturing.items', { returnObjects: true }),
      mockup: <AccountingMockup />,
    },
    {
      id: 'sales-orchestration',
      icon: <RocketLaunch sx={{ fontSize: 28 }} />,
      tag: t('website.features.modules.salesOrchestration.tag'),
      title: t('website.features.modules.salesOrchestration.title'),
      subtitle: t('website.features.modules.salesOrchestration.subtitle'),
      description: t('website.features.modules.salesOrchestration.description'),
      items: t('website.features.modules.salesOrchestration.items', { returnObjects: true }),
      mockup: <POSMockup />,
    },
    {
      id: 'ai-insights',
      icon: <AutoAwesome sx={{ fontSize: 28 }} />,
      tag: t('website.features.modules.aiInsights.tag'),
      title: t('website.features.modules.aiInsights.title'),
      subtitle: t('website.features.modules.aiInsights.subtitle'),
      description: t('website.features.modules.aiInsights.description'),
      items: t('website.features.modules.aiInsights.items', { returnObjects: true }),
      mockup: <ReportingMockup />,
    },
    {
      id: 'consolidation',
      icon: <AccountTree sx={{ fontSize: 28 }} />,
      tag: t('website.features.modules.consolidation.tag'),
      title: t('website.features.modules.consolidation.title'),
      subtitle: t('website.features.modules.consolidation.subtitle'),
      description: t('website.features.modules.consolidation.description'),
      items: t('website.features.modules.consolidation.items', { returnObjects: true }),
      mockup: <MultiCompanyMockup />,
    },
    {
      id: 'governance',
      icon: <HistoryEdu sx={{ fontSize: 28 }} />,
      tag: t('website.features.modules.governance.tag'),
      title: t('website.features.modules.governance.title'),
      subtitle: t('website.features.modules.governance.subtitle'),
      description: t('website.features.modules.governance.description'),
      items: t('website.features.modules.governance.items', { returnObjects: true }),
      mockup: <ReportingMockup />,
    },
    {
      id: 'trade',
      icon: <AccountBalance sx={{ fontSize: 28 }} />,
      tag: 'Trade & Logistics',
      title: 'Global Trade Management',
      subtitle: 'Manage Letters of Credit, Bank Guarantees, and Landed Costs',
      description: 'Streamline your international trade operations with comprehensive tools for managing Letters of Credit (LCs), Bank Guarantees, and calculating precise landed costs for accurate inventory valuation.',
      items: [
        'Letters of Credit (LC) Tracking',
        'Bank Guarantees Management',
        'Automated Landed Cost Allocation',
        'Incoterms & Shipping Management'
      ],
      mockup: <InventoryMockup />,
    },
    {
      id: 'eam',
      icon: <Business sx={{ fontSize: 28 }} />,
      tag: 'Enterprise Asset Management',
      title: 'Complete Asset Lifecycle',
      subtitle: 'Capitalize, Depreciate, and Maintain Enterprise Assets',
      description: 'Track the full lifecycle of your enterprise assets from acquisition and capitalization to depreciation, maintenance, and disposal.',
      items: [
        'Automated Asset Capitalization',
        'Straight-Line & Declining Balance Depreciation',
        'Asset Maintenance Scheduling',
        'Warranty & Insurance Tracking'
      ],
      mockup: <AccountingMockup />,
    },
    {
      id: 'budgeting',
      icon: <Assessment sx={{ fontSize: 28 }} />,
      tag: 'Budgeting & Forecasting',
      title: 'Strategic Financial Planning',
      subtitle: 'Plan, Track, and Control Enterprise Spending',
      description: 'Create detailed departmental budgets, track actuals against forecasts in real-time, and enforce spending controls with automated workflow approvals.',
      items: [
        'Multi-Scenario Budgeting',
        'Real-time Budget vs Actual Variance',
        'Automated Spend Control Workflows',
        'Departmental & Cost Center Planning'
      ],
      mockup: <ReportingMockup />
    },
    {
      id: 'lease-accounting',
      icon: <AccountBalance sx={{ fontSize: 28 }} />,
      tag: 'Lease Accounting (IFRS 16 / ASC 842)',
      title: 'Automated IFRS 16 & ASC 842 Lease Accounting',
      subtitle: 'Capitalize Right-of-Use Assets & Automate Amortization Schedules',
      description: 'Effortlessly model your operational and finance lease portfolio on-balance sheet. Calculate Present Value using Incremental Borrowing Rates (IBR), automate monthly depreciation and finance expense postings, and handle contract remeasurements (§45).',
      items: [
        'Automatic PV Calculation & Incremental Borrowing Rate (IBR)',
        'Right-of-Use (ROU) Asset Capitalization & Depreciation',
        'Automated Dual-Entry Monthly Amortization Journals',
        'Contract Modifications (§45) & Statutory Disclosures'
      ],
      mockup: <AccountingMockup />
    },
    {
      id: 'installment-sales',
      icon: <AttachMoney sx={{ fontSize: 28 }} />,
      tag: 'Installment Sales & BNPL (IFRS 15 / IFRS 9)',
      title: 'Flexible Installment Sales & BNPL Financing',
      subtitle: 'Financing Contracts, EIR Amortization & Credit Risk Staging',
      description: 'Empower retail and wholesale buyers with flexible installment contracts. FluxERP automatically segregates Day-1 cash revenue from unearned finance charges under IFRS 15, calculates actuarial Effective Interest Rates (EIR) via Newton-Raphson, tracks customer DPD across 3-stage ECL credit tiers, and allocates payments through strict waterfall rules.',
      items: [
        'Significant Financing Component & Day-1 IFRS 15 Revenue Split',
        'Newton-Raphson Effective Interest Rate (EIR) Schedules',
        'Multi-Tier Waterfall Payment Allocation & Penalty Tracking',
        'IFRS 9 3-Stage Expected Credit Loss (ECL) & Repossession Workflows'
      ],
      mockup: <POSMockup />
    },
  ];

  return (
    <WebsiteLayout>
      <SEO {...seo} />

      <HeroSection chip={t('website.features.heroTitle')}>
        <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
          <Typography variant="h1" sx={{ fontSize: { xs: 36, md: 56 }, fontWeight: 800, mb: 3, color: 'primary.contrastText' }}>
            {t('website.features.heroTitle')}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, lineHeight: 1.6, color: 'primary.contrastText' }}>
            {t('website.features.heroSubtitle')}
          </Typography>
        </Box>
      </HeroSection>

      {/* module rows */}
      {modules.map((module, index) => (
        <ModuleRow key={module.id} module={module} index={index} />
      ))}

      {/* AI Module Guide */}
      <Box sx={{
        py: { xs: 12, md: 16 },
        background: theme => theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #163300 0%, #0E0F0C 100%)'
          : 'linear-gradient(135deg, #f7ffef 0%, #ffffff 100%)',
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'divider',
        textAlign: 'center'
      }}>
        <Container maxWidth="md">
          <Typography variant="h2" sx={{ fontWeight: 800, mb: 3, fontSize: { xs: 32, md: 48 } }}>
            {t('website.features.cta.title')}
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 5, maxWidth: 750, mx: 'auto', lineHeight: 1.6 }}>
            {t('website.features.cta.subtitle')}
          </Typography>
          <Button
            variant="contained"
            size="large"
            component={Link}
            to="/demo"
            sx={{
              px: 8, py: 2.5, borderRadius: 4, fontWeight: 800, fontSize: 20,
              boxShadow: theme => `0 20px 40px ${theme.palette.primary.main}40`
            }}
          >
            {t('website.features.cta.bookDemo')}
          </Button>
        </Container>
      </Box>

      {/* bottom CTA */}
      <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', py: 12 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <AutoAwesome sx={{ fontSize: 56, mb: 3, color: 'primary.contrastText' }} />
            <Typography variant="h2" sx={{ fontSize: { xs: 32, md: 46 }, fontWeight: 800, mb: 3, color: 'primary.contrastText' }}>
              {t('website.features.bottomCta.title')}
            </Typography>
            <Typography variant="h6" sx={{ mb: 2, opacity: 0.85, color: 'primary.contrastText' }}>
              {t('website.features.bottomCta.subtitle')}
            </Typography>
          </Box>
        </Container>
      </Box>
    </WebsiteLayout>
  );
};

export default FeaturesPage;
